<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useTicketColumnsStore } from 'dashboard/stores/ticketColumns';
import TicketCard from '../components/TicketCard.vue';
import TicketDetailDialog from '../components/TicketDetailDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const ticketsStore = useTicketsStore();
const columnsStore = useTicketColumnsStore();

const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const ticketDetailRef = ref(null);
const localColumns = ref([]);
const ticketsByColumn = reactive({});
const newColumnName = ref('');
const renaming = reactive({});

const columns = computed(() => columnsStore.getColumns);

const syncColumns = () => {
  localColumns.value = columns.value.map(c => ({ ...c }));
};
const syncTickets = () => {
  columns.value.forEach(column => {
    ticketsByColumn[column.id] = ticketsStore
      .getTicketsByColumn(column.id)
      .slice()
      .sort((a, b) => b.id - a.id);
  });
};

watch(columns, () => {
  syncColumns();
  syncTickets();
});
watch(() => ticketsStore.getTicketsList, syncTickets, { deep: true });

const openEdit = ticket => {
  ticketDetailRef.value?.open(ticket);
};

const onUpdateTitle = async ({ id, title }) => {
  try {
    await ticketsStore.update({ id, ticket: { title } });
  } catch {
    useAlert(t('TICKETS.UPDATE.ERROR'));
  }
};

const onTicketDropped = async (evt, columnId) => {
  if (!evt.added) return;
  try {
    await ticketsStore.moveToColumn({
      id: evt.added.element.id,
      ticketColumnId: columnId,
    });
  } catch {
    useAlert(t('TICKETS.UPDATE.ERROR'));
    syncTickets();
  }
};

const persistColumnOrder = async () => {
  try {
    await columnsStore.reorder(localColumns.value.map(c => c.id));
  } catch {
    useAlert(t('TICKETS.COLUMN.ERROR'));
    syncColumns();
  }
};

const addColumn = async () => {
  const name = newColumnName.value.trim();
  if (!name) return;
  try {
    await columnsStore.create({ ticket_column: { name } });
    newColumnName.value = '';
  } catch {
    useAlert(t('TICKETS.COLUMN.ERROR'));
  }
};

const startRename = column => {
  renaming[column.id] = column.name;
};
const saveRename = async column => {
  const name = (renaming[column.id] || '').trim();
  delete renaming[column.id];
  if (!name || name === column.name) return;
  try {
    await columnsStore.update({ id: column.id, ticket_column: { name } });
  } catch {
    useAlert(t('TICKETS.COLUMN.ERROR'));
  }
};
const deleteColumn = async column => {
  try {
    await columnsStore.delete(column.id);
  } catch {
    useAlert(t('TICKETS.COLUMN.DELETE_ERROR'));
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  await Promise.all([columnsStore.get(), ticketsStore.get()]);
  syncColumns();
  syncTickets();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex items-center justify-between gap-3 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-lg font-semibold text-n-slate-12">
          {{ t('TICKETS.BOARD.TITLE') }}
        </h1>
        <router-link
          :to="{ name: 'tickets_dashboard_list', params: { accountId: route.params.accountId } }"
          class="text-sm font-medium text-n-slate-11 hover:text-n-slate-12"
        >
          {{ t('TICKETS.LIST.TITLE') }}
        </router-link>
      </div>
      <div class="flex items-center gap-2">
        <Input
          v-model="newColumnName"
          :placeholder="t('TICKETS.COLUMN.ADD_PLACEHOLDER')"
          size="sm"
          @keyup.enter="addColumn"
        />
        <Button
          :label="t('TICKETS.COLUMN.ADD')"
          icon="i-lucide-plus"
          color="slate"
          variant="outline"
          size="sm"
          :disabled="!newColumnName.trim()"
          @click="addColumn"
        />
      </div>
    </header>

    <Draggable
      v-model="localColumns"
      item-key="id"
      class="flex flex-1 gap-4 p-6 overflow-x-auto"
      handle=".column-drag-handle"
      :group="{ name: 'columns' }"
      @end="persistColumnOrder"
    >
      <template #item="{ element: column }">
        <div
          class="flex flex-col w-80 shrink-0 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-weak"
        >
          <div class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak">
            <span
              class="w-2.5 h-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: column.color || '#94a3b8' }"
            />
            <span
              class="cursor-grab column-drag-handle text-n-slate-10 hover:text-n-slate-11 i-lucide-grip-vertical size-4"
            />
            <input
              v-if="renaming[column.id] !== undefined"
              v-model="renaming[column.id]"
              class="flex-1 px-2 py-1 text-sm rounded-md outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
              @keyup.enter="saveRename(column)"
              @blur="saveRename(column)"
            />
            <h2
              v-else
              class="flex-1 text-sm font-semibold truncate cursor-text text-n-slate-12"
              @dblclick="startRename(column)"
            >
              {{ column.name }}
            </h2>
            <span
              class="px-1.5 text-xs rounded-full text-n-slate-11 bg-n-alpha-2 tabular-nums"
            >
              {{ (ticketsByColumn[column.id] || []).length }}
            </span>
            <Button
              icon="i-lucide-x"
              variant="ghost"
              color="slate"
              size="xs"
              :title="t('TICKETS.COLUMN.DELETE')"
              @click="deleteColumn(column)"
            />
          </div>

          <Draggable
            :list="ticketsByColumn[column.id]"
            :group="{ name: 'tickets' }"
            item-key="id"
            class="flex flex-col flex-1 gap-2 p-2.5 overflow-y-auto min-h-[4rem]"
            ghost-class="opacity-40"
            @change="onTicketDropped($event, column.id)"
          >
            <template #item="{ element: ticket }">
              <TicketCard
                :ticket="ticket"
                :account-labels="accountLabels"
                @click="openEdit"
                @update-title="onUpdateTitle"
              />
            </template>
          </Draggable>
        </div>
      </template>
    </Draggable>

    <TicketDetailDialog ref="ticketDetailRef" />
  </div>
</template>
